// =============================================================================
// TopPeriodsScreen - "Beste perioden" (Toppers), linked from the Analyse page
// =============================================================================
// Ranks days, weeks or months by omzet ex BTW (or tickets) over a long range:
// this year, last year, the last 12 months or everything. Same permission and
// same numbers as Analyse: one fn_admin_analytics_timeseries call at the
// chosen bucket, summed per bucket client-side (lib/analytics-top.ts).
//
// Datumbasis matters here: 'created' ranks by when the money came in (and
// includes gift-voucher sales, only for all locations), 'run' by when guests
// visited. Follows the global location filter. Clicking a row opens that
// period on the Analyse page (?g=&d=).
// =============================================================================
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { fetchAnalyticsTimeseries, type DateBasis, type TimeseriesRow } from '../lib/analytics';
import {
  isRunning,
  periodTitle,
  periodTotals,
  rankPeriods,
  summarize,
  topRangeFor,
  type TopBucket,
  type TopMetric,
  type TopRange,
} from '../lib/analytics-top';
import { experienceColorOf } from '../lib/analytics-colors';
import { formatVenueLong, formatVenueShort, todayVenue } from '../lib/dates';
import { formatError } from '../lib/errors';
import { useLocationFilter } from '../lib/location-context';
import { useHeaderSlot, type HeaderSlot } from '../lib/header-slot';

const BUCKETS: { key: TopBucket; label: string; noun: string }[] = [
  { key: 'day', label: 'Beste dagen', noun: 'dag' },
  { key: 'week', label: 'Beste weken', noun: 'week' },
  { key: 'month', label: 'Beste maanden', noun: 'maand' },
];

const RANGES: { key: TopRange; label: string }[] = [
  { key: 'this_year', label: 'Dit jaar' },
  { key: 'last_12', label: 'Laatste 12 maanden' },
  { key: 'last_year', label: 'Vorig jaar' },
  { key: 'all', label: 'Alles' },
];

const METRICS: { key: TopMetric; label: string }[] = [
  { key: 'revenue_ex_vat_cents', label: 'Omzet ex BTW' },
  { key: 'guests', label: 'Tickets' },
];

const SIZES = [10, 25, 50];

const PREFS_KEY = 'neoseum-admin-top-prefs';

interface Prefs {
  bucket: TopBucket;
  range: TopRange;
  metric: TopMetric;
  dateBasis: DateBasis;
  size: number;
}

// Ranking by money: default to the sales date, which is when the cash came in
// and the only basis that carries gift-voucher sales.
const DEFAULT_PREFS: Prefs = {
  bucket: 'day', range: 'this_year', metric: 'revenue_ex_vat_cents', dateBasis: 'created', size: 10,
};

function loadPrefs(): Prefs {
  try {
    const p = JSON.parse(window.localStorage.getItem(PREFS_KEY) ?? '{}') as Partial<Prefs>;
    return {
      bucket: BUCKETS.some((b) => b.key === p.bucket) ? p.bucket! : DEFAULT_PREFS.bucket,
      range: RANGES.some((r) => r.key === p.range) ? p.range! : DEFAULT_PREFS.range,
      metric: METRICS.some((m) => m.key === p.metric) ? p.metric! : DEFAULT_PREFS.metric,
      dateBasis: p.dateBasis === 'run' || p.dateBasis === 'created' ? p.dateBasis : DEFAULT_PREFS.dateBasis,
      size: SIZES.includes(p.size ?? 0) ? p.size! : DEFAULT_PREFS.size,
    };
  } catch {
    return DEFAULT_PREFS;
  }
}

function fmtEuro(cents: number): string {
  return `€ ${(cents / 100).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtValue(metric: TopMetric, v: number): string {
  return metric === 'revenue_ex_vat_cents' ? fmtEuro(v) : String(v);
}

export function TopPeriodsScreen() {
  const { locationId, locations, setLocationId } = useLocationFilter();
  const navigate = useNavigate();
  const prefs0 = useMemo(loadPrefs, []);
  const [bucket, setBucket] = useState<TopBucket>(prefs0.bucket);
  const [range, setRange] = useState<TopRange>(prefs0.range);
  const [metric, setMetric] = useState<TopMetric>(prefs0.metric);
  const [dateBasis, setDateBasis] = useState<DateBasis>(prefs0.dateBasis);
  const [size, setSize] = useState<number>(prefs0.size);

  useEffect(() => {
    try {
      window.localStorage.setItem(
        PREFS_KEY,
        JSON.stringify({ bucket, range, metric, dateBasis, size } satisfies Prefs),
      );
    } catch {
      /* ignore quota errors */
    }
  }, [bucket, range, metric, dateBasis, size]);

  const today = todayVenue();
  const { from, to } = useMemo(() => topRangeFor(range, bucket, today), [range, bucket, today]);

  const [rows, setRows] = useState<TimeseriesRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await fetchAnalyticsTimeseries(from, to, { locationId, bucket, dateBasis }));
      setError(null);
    } catch (e) {
      setError(formatError(e));
    } finally {
      setLoading(false);
    }
  }, [from, to, locationId, bucket, dateBasis]);

  useEffect(() => {
    void load();
  }, [load]);

  const totals = useMemo(() => periodTotals(rows), [rows]);
  const ranked = useMemo(() => rankPeriods(totals, metric, size), [totals, metric, size]);
  const summary = useMemo(() => summarize(totals, metric), [totals, metric]);
  const colorOf = useMemo(() => experienceColorOf(rows.map((r) => r.experience_name)), [rows]);
  const best = ranked[0]?.[metric] ?? 0;

  const noun = BUCKETS.find((b) => b.key === bucket)!.noun;
  const metricLabel = METRICS.find((m) => m.key === metric)!.label;
  const rangeLabel = RANGES.find((r) => r.key === range)!.label;
  const locationName = useMemo(() => {
    if (!locationId) return 'Alle locaties';
    const l = locations.find((x) => x.id === locationId);
    return l ? (l.display_name ?? l.name) : 'Locatie';
  }, [locationId, locations]);

  const open = useCallback(
    (period: string) => navigate(`/analytics?g=${bucket}&d=${period}`),
    [navigate, bucket],
  );

  // MUST stay memoized (see AnalyticsScreen): an unstable slot loops Layout.
  const headerSlot = useMemo<HeaderSlot>(() => {
    const bucketSelect = (
      <select
        className="analytics-select"
        aria-label="Periode"
        value={bucket}
        onChange={(e) => setBucket(e.target.value as TopBucket)}
      >
        {BUCKETS.map((b) => <option key={b.key} value={b.key}>{b.label}</option>)}
      </select>
    );
    const rangeSelect = (
      <select
        className="analytics-select"
        aria-label="Bereik"
        value={range}
        onChange={(e) => setRange(e.target.value as TopRange)}
      >
        {RANGES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
      </select>
    );
    const basisSelect = (
      <select
        className="analytics-select"
        aria-label="Datumbasis"
        title="Verkoopdatum = wanneer er betaald is (met vouchers); Bezoekdatum = wanneer de gasten kwamen"
        value={dateBasis}
        onChange={(e) => setDateBasis(e.target.value as DateBasis)}
      >
        <option value="created">Verkoopdatum</option>
        <option value="run">Bezoekdatum</option>
      </select>
    );
    const metricSelect = (
      <select
        className="analytics-select"
        aria-label="Statistiek"
        value={metric}
        onChange={(e) => setMetric(e.target.value as TopMetric)}
      >
        {METRICS.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
      </select>
    );
    const sizeSelect = (
      <select
        className="analytics-select"
        aria-label="Aantal"
        value={size}
        onChange={(e) => setSize(Number(e.target.value))}
      >
        {SIZES.map((n) => <option key={n} value={n}>Top {n}</option>)}
      </select>
    );
    return {
      row1: (
        <>
          {bucketSelect}
          {rangeSelect}
        </>
      ),
      desktopRow2: (
        <div className="analytics-row2">
          {basisSelect}
          {metricSelect}
          {sizeSelect}
          <Link className="btn-link analytics-refresh" to="/analytics">← Analyse</Link>
          <button className="btn-link" onClick={() => void load()}>Ververs</button>
        </div>
      ),
      overflowPanel: (close: () => void) => (
        <div className="cal-overflow">
          <label className="cal-overflow-row cal-overflow-row--field">
            <span className="cal-overflow-label">Basis</span>
            {basisSelect}
          </label>
          <label className="cal-overflow-row cal-overflow-row--field">
            <span className="cal-overflow-label">Statistiek</span>
            {metricSelect}
          </label>
          <label className="cal-overflow-row cal-overflow-row--field">
            <span className="cal-overflow-label">Aantal</span>
            {sizeSelect}
          </label>
          <Link className="cal-overflow-row" to="/analytics" onClick={close}>← Analyse</Link>
          <button
            type="button"
            className="cal-overflow-row"
            onClick={() => { void load(); close(); }}
          >
            ↻ Ververs
          </button>
        </div>
      ),
    };
  }, [bucket, range, dateBasis, metric, size, load]);
  useHeaderSlot(headerSlot);

  return (
    <div className="screen analytics-screen">
      <div className="muted analytics-scope">
        {locationName}
        {locationId ? (
          <button
            type="button"
            className="analytics-scope-clear"
            onClick={() => setLocationId(null)}
            aria-label="Terug naar alle locaties"
          >
            × alle locaties
          </button>
        ) : null}
        {' · '}{rangeLabel} ({formatVenueShort(from)} – {formatVenueShort(to)} {to.slice(0, 4)})
        {' · '}{dateBasis === 'created' ? 'op verkoopdatum' : 'op bezoekdatum'}
      </div>

      {error ? <div className="error-card">{error}</div> : null}
      {loading ? <div className="muted">Laden…</div> : null}

      {!loading && !error ? (
        <>
          <div className="analytics-cards">
            <div className="analytics-card">
              <div className="analytics-card-label">Beste {noun}</div>
              <div className="analytics-card-value">{fmtValue(metric, best)}</div>
            </div>
            <div className="analytics-card">
              <div className="analytics-card-label">Gemiddeld per {noun}</div>
              <div className="analytics-card-value">{fmtValue(metric, summary.average)}</div>
            </div>
            <div className="analytics-card">
              <div className="analytics-card-label">Mediaan</div>
              <div className="analytics-card-value">{fmtValue(metric, summary.median)}</div>
            </div>
          </div>

          <section className="analytics-section">
            <h2 className="analytics-h2">
              Top {size} {bucket === 'day' ? 'dagen' : bucket === 'week' ? 'weken' : 'maanden'} · {metricLabel}
            </h2>
            {ranked.length === 0 ? (
              <div className="muted">Geen data in dit bereik.</div>
            ) : (
              <div className="analytics-loc-table-wrap">
                <table className="payments-table analytics-table top-table" aria-label={`Beste ${noun}`}>
                  <thead>
                    <tr>
                      <th scope="col" className="top-rank">#</th>
                      <th scope="col">Periode</th>
                      <th scope="col" className="top-bar-col" aria-label="Verdeling per experience" />
                      <th scope="col" className="payments-cell-amount">Omzet ex BTW</th>
                      <th scope="col" className="payments-cell-amount">Tickets</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ranked.map((t, i) => (
                      <tr
                        key={t.period}
                        className="top-row"
                        tabIndex={0}
                        title="Open deze periode in Analyse"
                        onClick={() => open(t.period)}
                        onKeyDown={(e) => { if (e.key === 'Enter') open(t.period); }}
                      >
                        <td className="top-rank">{i + 1}</td>
                        <td className="top-period">
                          {periodTitle(bucket, t.period, formatVenueLong)}
                          {isRunning(bucket, t.period, today) ? <span className="top-running">lopend</span> : null}
                        </td>
                        <td className="top-bar-col">
                          <div
                            className="top-bar"
                            style={{ width: `${best > 0 ? (t[metric] / best) * 100 : 0}%` }}
                          >
                            {t.byExperience
                              .filter((e) => e[metric] > 0)
                              .map((e) => (
                                <span
                                  key={e.name}
                                  title={`${e.name}: ${fmtValue(metric, e[metric])}`}
                                  style={{ flexGrow: e[metric], background: colorOf(e.name) }}
                                />
                              ))}
                          </div>
                        </td>
                        <td className="payments-cell-amount">{fmtEuro(t.revenue_ex_vat_cents)}</td>
                        <td className="payments-cell-amount">{t.guests}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="muted analytics-loc-note">
              Omzet = geïnde betalingen ex BTW, zoals op Analyse. Tickets tellen verkochte vouchers mee, ingewisselde vouchers niet.
              {dateBasis === 'created'
                ? (locationId ? ' Voucherverkoop hoort bij geen locatie en telt hier dus niet mee.' : ' Voucherverkoop telt op de verkoopdag.')
                : ' Op bezoekdatum telt voucherverkoop niet mee (een voucher heeft geen bezoekdag).'}
              {' '}Gemiddelde en mediaan over de {summary.count} {bucket === 'day' ? 'dagen' : bucket === 'week' ? 'weken' : 'maanden'} met
              {metric === 'revenue_ex_vat_cents' ? ' omzet' : ' tickets'}. Klik een regel om die periode in Analyse te openen.
            </p>
          </section>
        </>
      ) : null}
    </div>
  );
}
